import React, { useState, useEffect } from 'react';
import { 
  RiHistoryLine, 
  RiTimeLine,
  RiFileTextLine,
  RiUserLine
} from 'react-icons/ri';
import { rawApi as api } from '../../utils/apiWithLogging';
import { usePermission } from '../../hooks/usePermission';
import { formatDateTime } from '../../utils/dateUtils';

interface ContractActivityLog {
  id: number;
  action: string;
  description?: string;
  created_at: string;
  user_name?: string;
  user_email?: string;
  user_id: number;
}

interface ContractHistorySectionProps {
  contractId: number;
}

const ContractHistorySection: React.FC<ContractHistorySectionProps> = ({ contractId }) => {
  /**
   * `GET /contracts/{id}/activity-logs` gate ด้วย `contracts.view` — การ์ดนี้ mount
   * บน**หน้ารายละเอียดการเช่า**ด้วย (ไม่ใช่แค่หน้าสัญญา) ซึ่ง guard แค่ `rentals.view`
   * → role ที่ดูการเช่าได้แต่ไม่มีสิทธิ์สัญญาจะได้ 403 ทุกครั้งที่เปิดหน้า (บั๊กเดียวกับ BUG-UI-09)
   */
  const { hasPermission } = usePermission();
  const canView = hasPermission('contracts.view');
  const [activityLogs, setActivityLogs] = useState<ContractActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    fetchContractHistory();
  }, [contractId, canView]);

  const fetchContractHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/contracts/${contractId}/activity-logs`);
      setActivityLogs(response.data || []);
    } catch (error: any) {
      console.error('Error fetching contract history:', error);
      setError('ไม่สามารถโหลดประวัติสัญญาได้');
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = (action: string): string => {
    const actionLabels: { [key: string]: string } = {
      'CREATE': 'สร้างสัญญา',
      'UPDATE': 'แก้ไขสัญญา',
      'APPROVE': 'อนุมัติสัญญา',
      'REJECT': 'ปฏิเสธสัญญา',
      'SIGN': 'ลงนามสัญญา',
      'ACTIVATE': 'เปิดใช้งานสัญญา',
      'EXPIRE': 'สัญญาหมดอายุ',
      'TERMINATE': 'ยกเลิกสัญญา',
      'RENEW': 'ต่ออายุสัญญา',
      'SUSPEND': 'ระงับสัญญา',
      // action ที่ backend บันทึกจริง (crud/contract.py approve/terminate/expire, endpoints amend/delete)
      // แต่เดิมไม่มี label → โชว์โค้ดดิบ "STATUS_CHANGE" (E2E v4 OBS-CT-TIMELINE-LABEL; แนวเดียวกับ ef72a0e ฝั่ง rental)
      'STATUS_CHANGE': 'เปลี่ยนสถานะสัญญา',
      'AMEND': 'แก้ไขเพิ่มเติมสัญญา',
      'DELETE': 'ลบสัญญา',
      'RESTORE': 'กู้คืนสัญญา'
    };
    return actionLabels[action] || action;
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE':
        return <RiFileTextLine className="h-4 w-4 text-green-500" />;
      case 'UPDATE':
        return <RiTimeLine className="h-4 w-4 text-blue-500" />;
      case 'APPROVE':
        return <RiUserLine className="h-4 w-4 text-green-500" />;
      case 'SIGN':
        return <RiFileTextLine className="h-4 w-4 text-blue-500" />;
      default:
        return <RiTimeLine className="h-4 w-4 text-gray-500" />;
    }
  };

  // ไม่มีสิทธิ์ดูสัญญา → ซ่อนการ์ดทั้งใบ
  if (!canView) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <RiHistoryLine className="mr-2 h-5 w-5 text-gray-400" />
          ประวัติสัญญา
        </h3>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">กำลังโหลดประวัติ...</p>
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <p className="text-sm text-red-500">{error}</p>
            <button
              onClick={fetchContractHistory}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800"
            >
              ลองใหม่
            </button>
          </div>
        ) : activityLogs.length > 0 ? (
          <div className="flow-root">
            <ul className="-mb-8">
              {activityLogs.map((log, logIdx) => (
                <li key={log.id}>
                  <div className="relative pb-8">
                    {logIdx !== activityLogs.length - 1 && (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    )}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white">
                          {getActionIcon(log.action)}
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                        <div>
                          <p className="text-sm text-gray-900">
                            {getActionLabel(log.action)}
                            {log.description && (
                              <span className="text-gray-500"> - {log.description}</span>
                            )}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            โดย {log.user_name || log.user_email || 'ระบบ'}
                          </p>
                        </div>
                        <div className="whitespace-nowrap text-right text-xs text-gray-500">
                          {formatDateTime(log.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">
            ไม่มีประวัติการดำเนินการของสัญญา
          </p>
        )}
      </div>
    </div>
  );
};

export default ContractHistorySection;